/**
 * HOME = the launcher, as in ASSIST-MNH: tiles grouped by what the provider is doing, each with
 * its service's icon and colour and a live count. Adding a care programme adds a tile, not a tab.
 */
import { formatEth } from '@assist/core';
import { clock } from '../controller.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks } from '../packs.js';
import {
  Section,
  Tile,
  activeProgrammeList,
  programmeIcon,
  programmeTitle,
  useCounts,
} from '../shell.js';
import { HUE } from '../ui/icons.js';

export function Home() {
  const a = useApp();
  const u = a.session?.user;
  const counts = useCounts();
  const packs = activePacks(a.packSettings).map((x) => x.pack);
  if (!u) return null;
  const late = counts ? counts.overdue : 0;
  return (
    <div class="card">
      <h3>{t.welcome(u.fullName)}</h3>
      <p class="muted">
        {[t.roleName[u.role] ?? u.role, u.facilityName, formatEth(clock.todayEth())]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <Section label={t.grpCare}>
        <Tile href="#/register" icon="register" title={t.registerPatient} sub={t.subRegister} />
        <Tile href="#/find" icon="find" title={t.navFind} sub={t.subFind} />
        <Tile
          href="#/followup"
          icon="worklists"
          title={t.navFollowup}
          sub={counts ? t.subFollowup(counts.overdue, counts.due) : t.computing}
          alert={late > 0}
        />
      </Section>
      <Section label={t.grpProgrammes}>
        {packs.length ? (
          activeProgrammeList(packs).map(({ pack, programme }) => (
            <Tile
              key={programme.code}
              href={`#/programme/${programme.code}`}
              icon={programmeIcon(pack, programme)}
              hue={HUE[programmeIcon(pack, programme)]}
              title={programmeTitle(programme)}
              sub={counts ? t.inCare(counts.inCare[programme.code] ?? 0) : t.computing}
            />
          ))
        ) : (
          <p class="muted">{t.noPacks}</p>
        )}
      </Section>
      <Section label={t.grpReports}>
        <Tile href="#/numbers" icon="dashboard" title={t.navNumbers} sub={t.subNumbers} />
        <Tile href="#/hmis" icon="registers" title={t.hmisTitle} sub={t.subHmis} />
        {packs.some((p) => p.registers?.length) ? (
          <Tile href="#/registers" icon="export" title={t.registersTitle} sub={t.subRegisters} />
        ) : null}
      </Section>
      <Section label={u.role === 'admin' ? t.grpAdmin : t.grpYou}>
        {u.role === 'admin' ? (
          <Tile href="#/settings" icon="admin" title={t.settings} sub={t.subSettings} />
        ) : null}
        {a.status?.failed ? (
          <Tile
            href="#/failed"
            icon="sync"
            title={t.failedTitle}
            sub={t.failedN(a.status.failed)}
            alert
          />
        ) : null}
        <Tile href="#/me" icon="account" title={t.navMe} sub={t.subMe} />
      </Section>
    </div>
  );
}
